// src/pages/payroll/InsuranceRateManage.jsx
// 4대보험 요율 관리 — 연도별 국민연금/건강보험/장기요양/고용보험율(근로자 부담분).
// 용역사업 지출 인건비 빠른 입력(ExpenseServiceNew)에서 급여합계 기준 자동계산에 쓴다.
// 연말에 새 요율이 발표되면 여기서 새 연도 행만 추가하면 다음 해부터 자동 적용된다.
import React, { useEffect, useState } from "react";
import api from "../../api";

const num = (v) => {
  const n = Number(String(v ?? "0").replace(/[, %]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export default function InsuranceRateManage() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const res = await api.get("insurance/rates/");
      const list = Array.isArray(res.data) ? res.data : res.data.results || [];
      setRows(list);
      const e = {};
      list.forEach((r) => {
        e[r.id] = {
          pension_rate: String(r.pension_rate),
          health_rate: String(r.health_rate),
          care_rate: String(r.care_rate),
          employ_rate: String(r.employ_rate),
          // ⚠️ 산재는 null 허용(미등록) — null을 "null" 문자열로 만들지 않는다.
          injury_rate: r.injury_rate == null ? "" : String(r.injury_rate),
        };
      });
      setEdit(e);
    } catch (err) {
      setMsg("불러오기 실패: " + (err?.response?.data?.detail || err.message));
    }
  };

  useEffect(() => { load(); }, []);

  const addYear = async () => {
    const v = window.prompt("추가할 연도 (예: 2027)");
    const year = num(v);
    if (!year) return;
    if (rows.some((r) => r.year === year)) {
      setMsg(`${year}년 행이 이미 있습니다.`);
      return;
    }
    setBusy(true);
    try {
      const latest = rows[0];
      await api.post("insurance/rates/", {
        year,
        pension_rate: latest?.pension_rate ?? 0,
        health_rate: latest?.health_rate ?? 0,
        care_rate: latest?.care_rate ?? 0,
        employ_rate: latest?.employ_rate ?? 0,
        injury_rate: latest?.injury_rate ?? null,
      });
      await load();
      setMsg(`${year}년 행을 추가했습니다(전년도 값 복사). 확인 후 저장하세요.`);
    } catch (err) {
      setMsg("추가 실패: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const saveRow = async (row) => {
    setBusy(true);
    try {
      const e = edit[row.id];
      await api.patch(`insurance/rates/${row.id}/`, {
        pension_rate: num(e.pension_rate),
        health_rate: num(e.health_rate),
        care_rate: num(e.care_rate),
        employ_rate: num(e.employ_rate),
        // 빈 칸은 null(미등록)로 — 0%와 구분해야 견적서가 "채우지 않음"을 알 수 있다.
        injury_rate: String(e.injury_rate).trim() === "" ? null : num(e.injury_rate),
      });
      await load();
      setMsg(`${row.year}년 요율을 저장했습니다.`);
    } catch (err) {
      setMsg("저장 실패: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const S = {
    wrap: { padding: 16, fontSize: 13, color: "#1f2937" },
    btn: (c, d) => ({
      height: 32, padding: "0 14px", background: d ? "#9ca3af" : c, color: "#fff",
      border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer",
      fontSize: 12, whiteSpace: "nowrap",
    }),
    th: { background: "#f1f5f9", padding: "8px 10px", borderBottom: "1px solid #cbd5e1", textAlign: "center", whiteSpace: "nowrap" },
    td: { padding: "6px 10px", borderBottom: "1px solid #eef2f7", textAlign: "center" },
    input: {
      width: 90, height: 30, padding: "2px 6px", borderRadius: 6, border: "1px solid #cbd5e1",
      background: "#ffffff", textAlign: "right", boxSizing: "border-box",
    },
    card: { border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, maxWidth: 760 },
  };

  return (
    <div style={S.wrap}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, whiteSpace: "nowrap" }}>4대보험 요율 관리</h2>
      <p style={{ color: "#6b7280", marginBottom: 14 }}>
        연도별 국민연금·건강보험·장기요양·고용보험 근로자 부담분 요율(%)입니다. 장기요양은 건강보험료 대비 %입니다.
        용역사업 지출 인건비 빠른 입력에서 작성일 연도 기준으로 조회하며, 해당 연도가 없으면 가장 최근 연도 값을 씁니다.
      </p>

      {msg && (
        <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", padding: "6px 10px", borderRadius: 6, marginBottom: 12, color: "#075985" }}>
          {msg}
        </div>
      )}

      <div style={S.card}>
        <div style={{ marginBottom: 10 }}>
          <button style={S.btn("#0891b2", busy)} disabled={busy} onClick={addYear}>+ 연도 추가</button>
        </div>

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th}>연도</th>
                <th style={S.th}>국민연금율(%)</th>
                <th style={S.th}>건강보험율(%)</th>
                <th style={S.th}>장기요양율(%, 건강보험료 대비)</th>
                <th style={S.th}>고용보험율(%)</th>
                <th style={S.th}>산재보험율(%)</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td style={S.td} colSpan={7}>등록된 연도가 없습니다.</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{row.year}년</td>
                  {["pension_rate", "health_rate", "care_rate", "employ_rate", "injury_rate"].map((field) => (
                    <td style={S.td} key={field}>
                      <input
                        type="text"
                        style={S.input}
                        value={edit[row.id]?.[field] ?? ""}
                        placeholder={field === "injury_rate" ? "미등록" : ""}
                        title={field === "injury_rate"
                          ? "사업장·업종마다 다릅니다. 비워두면 견적서에서 산재 칸을 자동으로 채우지 않습니다."
                          : ""}
                        onChange={(e) =>
                          setEdit((p) => ({ ...p, [row.id]: { ...p[row.id], [field]: e.target.value } }))
                        }
                        onKeyDown={(e) => e.key === "Enter" && saveRow(row)}
                      />
                    </td>
                  ))}
                  <td style={S.td}>
                    <button style={S.btn("#16a34a", busy)} disabled={busy} onClick={() => saveRow(row)}>저장</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
